import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, RefreshCw, Play } from "lucide-react";
import { GARDIENS } from "@/data/gardiens";


function VoicePreview() {
  const [gid, setGid] = useState("arsene");
  const [busy, setBusy] = useState(false);
  const audio = useRef(null);
  const play = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gardien-voices/preview", { gardien_id: gid });
      audio.current?.pause();
      audio.current = new Audio(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      audio.current.play().catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Écoute impossible."); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={gid} onValueChange={setGid}>
        <SelectTrigger className="w-56 rounded-xl" data-testid="gardien-voice-preview-select"><SelectValue /></SelectTrigger>
        <SelectContent>{GARDIENS.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
      </Select>
      <Button variant="outline" onClick={play} disabled={busy} data-testid="gardien-voice-preview-play">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />} Écouter
      </Button>
    </div>
  );
}

function VoiceChoice({ sp, v, current, playing, onListen, onChoose, busy }) {
  return (
    <div className={`rounded-xl border p-3 flex items-center gap-2 ${current ? "border-emerald-400 bg-emerald-50" : "border-gray-200"}`} data-testid={`voice-option-${sp.id}-${v.id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-800 truncate">{v.name}</p>
        <p className="text-xs text-gray-500 truncate">{v.desc}{current ? " · voix actuelle" : ""}</p>
      </div>
      <Button variant="outline" size="sm" onClick={() => onListen(v.id)} disabled={!!busy} data-testid={`voice-listen-${sp.id}-${v.id}`}>
        {busy === `listen-${v.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className={`w-4 h-4 ${playing ? "text-emerald-600" : ""}`} />}
      </Button>
      {!current && (
        <Button size="sm" onClick={() => onChoose(v.id)} disabled={!!busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`voice-choose-${sp.id}-${v.id}`}>Choisir</Button>
      )}
    </div>
  );
}

function SpeakerRow({ sp, voices, remaining, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [playing, setPlaying] = useState("");
  const audio = useRef(null);
  useEffect(() => () => audio.current?.pause(), []);
  const listen = async (vid) => {
    setBusy(`listen-${vid}`);
    try {
      const { data } = await api.post("/admin/gardien-voices/preview", { gardien_id: sp.id, voice_id: vid });
      audio.current?.pause();
      audio.current = new Audio(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      audio.current.onended = () => setPlaying("");
      audio.current.play().then(() => setPlaying(vid)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Écoute impossible."); }
    finally { setBusy(""); }
  };
  const choose = async (vid) => {
    setBusy("save");
    try {
      await api.post("/admin/gardien-voices/assign", { speaker: sp.id, voice_id: vid });
      toast.success(`Nouvelle voix pour ${sp.name}. Ses phrases seront refaites peu à peu, chaque matin.`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible."); }
    finally { setBusy(""); }
  };
  const mine = voices.filter((v) => v.gender === (sp.female ? "female" : "male"));
  const cur = voices.find((v) => v.id === sp.voice_id);
  return (
    <div className="py-3 border-b border-gray-100 last:border-0" data-testid={`voice-row-${sp.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center gap-2 text-left" data-testid={`voice-row-toggle-${sp.id}`}>
        <span className="w-28 font-semibold text-gray-800">{sp.name}</span>
        <span className="text-sm text-gray-600">{cur ? `${cur.name} · ${cur.desc}` : sp.voice_id}</span>
        {remaining > 0 && <span className="text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" data-testid={`voice-remaining-${sp.id}`}>{remaining} phrase(s) à refaire</span>}
        <span className="ml-auto text-sm text-emerald-700">{open ? "Fermer" : `Écouter les ${mine.length} voix`}</span>
      </button>
      {open && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
          {mine.map((v) => <VoiceChoice key={v.id} sp={sp} v={v} current={v.id === sp.voice_id} playing={playing === v.id} onListen={listen} onChoose={choose} busy={busy} />)}
        </div>
      )}
    </div>
  );
}

function VoicePicker() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [remaining, setRemaining] = useState({});
  const load = useCallback(() => {
    api.get("/admin/gardien-voices/remaining").then((r) => setRemaining(r.data)).catch(() => {});
    return api.get("/admin/gardien-voices/voices").then((r) => setData(r.data)).catch(() => setData({ voices: [], speakers: [] }));
  }, []);
  useEffect(() => { if (open && !data) load(); }, [open, data, load]);
  return (
    <div className="rounded-xl border border-gray-200 p-4" data-testid="voice-picker">
      <button onClick={() => setOpen((o) => !o)} className="font-semibold text-emerald-800" data-testid="voice-picker-toggle">
        {open ? "▾" : "▸"} Choisir la voix de chaque Gardien (Gemini)
      </button>
      {open && (
        <div className="mt-3">
          <p className="text-xs text-gray-500 mb-2">Ouvrez un Gardien, écoutez-le avec chaque voix, puis choisissez. Chaque premier essai compte une voix dans le quota du jour (il est ensuite gardé). Ses phrases sont refaites peu à peu chaque matin ; l'ancienne voix reste en attendant.</p>
          {!data ? <Loader2 className="w-5 h-5 animate-spin text-emerald-700" /> :
            data.speakers.map((sp) => <SpeakerRow key={`${sp.id}-${sp.voice_id}`} sp={sp} voices={data.voices} remaining={remaining[["rose", "auguste"].includes(sp.id) ? "rose-auguste" : sp.id] || 0} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}

export default function GardienVoicesCard() {
  const [st, setSt] = useState(null);
  const load = useCallback(() => api.get("/admin/gardien-voices/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const run = async (path, ok) => {
    try { await api.post(path); toast.success(ok); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action impossible."); }
  };

  return (
    <Card className="rounded-2xl mt-6" data-testid="gardien-voices-card">
      <CardContent className="p-6 space-y-5">
        <div>
          <h3 className="font-serif-display text-2xl">Voix des Gardiens</h3>
          <p className="text-sm text-gray-500 mt-1">Arsène lit les contes ; chaque Gardien félicite et encourage. Les textes sont écrits par Gemini.</p>
          {st && (
            <p className="text-sm text-gray-700 mt-2" data-testid="gardien-voices-status">
              {!st.key ? "Clé GEMINI_API_KEY manquante." : `Contes lus par Arsène : ${st.stories_ready}/${st.stories} · Félicitations : ${st.congrats_ready}/${st.congrats_target} · Encouragements : ${st.encourage_ready}/${st.encourage_target}`}
              {st.working && <span className="inline-flex items-center gap-1 ml-2 text-amber-700"><Loader2 className="w-3 h-3 animate-spin" /> en cours</span>}
            </p>
          )}
        </div>
        <p className="text-sm text-gray-600" data-testid="gardien-voice-engine">Voix utilisée : Gemini (les bruitages des jeux sonores restent faits avec ElevenLabs).</p>
        <VoicePreview />
        <VoicePicker />
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => run("/admin/gardien-voices/regenerate", "Les Gardiens réenregistrent leurs voix. Cela peut prendre quelques minutes.")}
            disabled={!st?.key || st?.working} data-testid="gardien-voices-regenerate"><RefreshCw className="w-4 h-4 mr-2" /> Régénérer toutes les voix</Button>
          <Button variant="outline" onClick={() => run("/admin/gardien-voices/prepare", "Les Gardiens préparent les voix manquantes.")}
            disabled={!st?.key || st?.working} data-testid="gardien-voices-prepare"><Sparkles className="w-4 h-4 mr-2" /> Compléter les voix manquantes</Button>
        </div>
      </CardContent>
    </Card>
  );
}
